import {Popover as ArkPopover} from '@ark-ui/react/popover'
import {Portal as ArkPortal} from '@ark-ui/react'
import {useT} from '@eitje/i18n'
import {EitjeButton} from '~/components/eitje_button'
import {Layout} from '~/components/layout'
import {Text} from '~/components/text'
import {TimePickerPanelColumn} from '~/components/time_picker/components'
import {useTimePicker} from './use_time_picker'
import './index.less'

/**
 * @typedef {Object} TimePickerContextValues
 * @property {string} value Selected time formatted as `HH:mm`, or an empty string when no time is selected.
 * @property {string} lastSelectedValue Most recently selected time formatted as `HH:mm`.
 * @property {(value: string) => boolean} validateTimeFn Returns whether a time can be selected.
 * @property {(value: string) => void} setValue Commits a selectable time formatted as `HH:mm`.
 * @property {(state: boolean) => void} setOpen
 */

/**
 * @typedef {Object} TimePickerProps
 * @property {string} [value] Controlled selected time formatted as `HH:mm`.
 * @property {(value: string) => void} onChange Called with the selected time formatted as `HH:mm`.
 * @property {string} [defaultOpenValue] Time selected initially when the picker opens without a value.
 * @property {number} [minuteStep] Interval between minute options. Defaults to five minutes.
 * @property {string} [minTime] Earliest selectable time, inclusively.
 * @property {string} [maxTime] Latest selectable time, inclusively.
 * @property {(hour: number, minute: number) => boolean} [disabledTimeFn] Returns whether a time cannot be selected.
 * @property {(timePicker: TimePickerContextValues) => import('react').ReactNode} [renderTrigger] Renders a custom trigger.
 * @property {(open: boolean) => void} [onOpenChange] Reports whether the picker is open.
 * @property {boolean | string | {key: string}} [disabled]
 * Prevents interaction with the picker. The default trigger displays a string or translation as a tooltip.
 * @property {string} [placeholder] Placeholder shown by the default trigger when no time is selected.
 */

/** @param {TimePickerProps} props */
export const TimePicker = ({
	value,
	minTime,
	maxTime,
	defaultOpenValue,
	minuteStep,
	disabled,
	placeholder,
	onChange,
	onOpenChange,
	disabledTimeFn,
	renderTrigger,
}) => {
	const {t} = useT()
	const {
		displayValue,
		hourOptions,
		minuteOptions,
		disabledHours,
		disabledMinutes,
		lastSelectedHour,
		lastSelectedMinute,
		allowReselect,
		timePickerContext,
		handleOpenChange,
		handleSelectHour,
		handleSelectMinute,
	} = useTimePicker({value, defaultOpenValue, minuteStep, minTime, maxTime, disabledTimeFn, onChange, onOpenChange})

	let triggerElement = (
		<ArkPopover.Trigger asChild>
			<EitjeButton dropdown disabled={disabled}>
				<Text>{displayValue || placeholder}</Text>
			</EitjeButton>
		</ArkPopover.Trigger>
	)

	if (renderTrigger) {
		triggerElement = (
			<ArkPopover.Context>
				{popoverCtx => (
					<ArkPopover.Trigger asChild disabled={disabled}>
						{renderTrigger({...timePickerContext, setOpen: popoverCtx.setOpen})}
					</ArkPopover.Trigger>
				)}
			</ArkPopover.Context>
		)
	}

	return (
		<ArkPopover.Root
			positioning={{placement: 'bottom-start', overlap: true}}
			onOpenChange={handleOpenChange}
			// Prevent `escape` from closing the modal when it is in one
			onEscapeKeyDown={event => event.stopPropagation()}
			autoFocus={false}
			lazyMount
			unmountOnExit
		>
			{triggerElement}
			<ArkPortal>
				<ArkPopover.Positioner>
					<ArkPopover.Content className="time-picker-panel" aria-label={placeholder || t('select')}>
						<Layout className="time-picker-panel-columns" gap={0}>
							<ArkPopover.Context>
								{popoverCtx => (
									<>
										<TimePickerPanelColumn
											allOptions={hourOptions}
											disabledOptions={disabledHours}
											label={t('common.hour')}
											selectedValue={lastSelectedHour}
											allowReselect={allowReselect}
											onSelect={handleSelectHour}
										/>
										<TimePickerPanelColumn
											allOptions={minuteOptions}
											disabledOptions={disabledMinutes}
											label={t('common.minute_abbr')}
											selectedValue={lastSelectedMinute}
											allowReselect={allowReselect}
											onSelect={value => {
												handleSelectMinute(value)
												popoverCtx.setOpen(false)
											}}
										/>
									</>
								)}
							</ArkPopover.Context>
						</Layout>
					</ArkPopover.Content>
				</ArkPopover.Positioner>
			</ArkPortal>
		</ArkPopover.Root>
	)
}
